import { FileSignature } from 'lucide-react';

/**
 * Ce que le poseur voit en arrivant : ses chiffres à lui, et un gros bouton.
 *
 * Le poseur travaille debout, souvent sur tablette, chez un client qui attend. L'écran
 * ne lui propose donc qu'une seule action mise en avant — démarrer un chantier — et
 * range le reste en dessous. Les compteurs ne portent que SES chantiers : c'est la
 * route `/poseur/home-stats` qui filtre, pas l'affichage.
 */
export default function AccueilPoseur({ stats, user, t, onNouveauChantier, onVoirChantiers }) {
  const derniers = stats?.derniers_chantiers || [];

  return (
    <div className="space-y-5" data-testid="poseur-home">
      <div className="bg-gradient-to-br from-marque to-marque-clair text-white rounded-xl p-5 shadow-md">
        <p className="text-[13px] text-white/60">{t('poseur_hello')}</p>
        <h1 className="text-2xl font-semibold mt-0.5" data-testid="poseur-welcome-name">
          {stats?.user_prenom || user?.name || '—'} 👋
        </h1>
        <p className="text-[12px] text-white/60 mt-1">{t('poseur_welcome_msg')}</p>
        <div className="grid grid-cols-2 gap-3 mt-4">
          <div className="bg-white/10 rounded-lg p-3">
            <p className="text-[10px] text-white/60 uppercase tracking-wider">{t('poseur_stat_month')}</p>
            {/* « — » et non 0 : tant que la route n'a pas répondu, on ne sait pas.
                Afficher 0 annoncerait un mois vide qui n'a jamais été constaté. */}
            <p className="text-2xl font-bold mt-0.5" data-testid="poseur-stat-month">
              {stats?.chantiers_ce_mois ?? '—'}
            </p>
          </div>
          <div className="bg-white/10 rounded-lg p-3">
            <p className="text-[10px] text-white/60 uppercase tracking-wider">{t('poseur_stat_total')}</p>
            <p className="text-2xl font-bold mt-0.5" data-testid="poseur-stat-total">
              {stats?.total_chantiers ?? '—'}
            </p>
          </div>
        </div>
      </div>

      {/* ⚠️ LE COMPTE « EN ATTENTE DE MATERIEL » A ETE RETIRE LE 10/09/2026.
          Un bandeau orange l affichait ici depuis le 30/08. Demande de Boubacar :
          « ils ne doivent pas voir en attente de materiel. Toutes ces infos remontent
          dans les alertes pour l admin operateur ». Le manque n a pas disparu, c est
          son destinataire qui change. Ne pas le remettre : il reviendrait doubler
          l alerte de l admin d une information que le terrain ne doit plus suivre. */}

      <button
        onClick={onNouveauChantier}
        className="w-full py-5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl font-semibold text-lg flex items-center justify-center gap-3 shadow-md active:scale-[0.98] transition-transform"
        data-testid="poseur-new-chantier-btn"
      >
        <FileSignature className="w-6 h-6" />
        {t('poseur_new_chantier_btn')}
      </button>

      <div className="bg-white rounded-xl border border-neutral-200 p-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-[13px] font-semibold text-neutral-800">{t('poseur_last_chantiers')}</h2>
          <button
            onClick={onVoirChantiers}
            className="text-[11px] text-marque hover:underline font-medium min-h-[44px] -my-2 px-2 -mr-2 flex items-center"
            data-testid="poseur-see-all-chantiers"
          >
            {t('poseur_see_all')} →
          </button>
        </div>
        {derniers.length === 0 ? (
          <p className="text-[12px] text-neutral-400 py-3 text-center">{t('poseur_no_chantiers')}</p>
        ) : (
          <ul className="divide-y divide-neutral-100">
            {derniers.map((c) => {
              const signe = c.statut_mandat === 'signe' || c.statut_mandat === 'actif';
              return (
                <li key={c.client_id} className="py-2.5 flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-[12.5px] font-medium text-neutral-800 truncate">{c.client_nom || '—'}</p>
                    <p className="text-[10.5px] text-neutral-500 truncate">
                      {c.reference_chantier || '—'} ·{' '}
                      {c.created_at ? new Date(c.created_at).toLocaleDateString('fr-FR') : '—'}
                    </p>
                  </div>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                    signe ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'
                  }`}>
                    {signe ? t('status_signed') : t('status_pending')}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
